import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import api, { formatApiError } from "@/lib/api";
import { Loader2, ChevronLeft, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

export default function CreateProjectPage() {
  const { id } = useParams(); // present when editing
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { language } = useI18n();
  const isMunicipality = user && (user.role === "admin" || user.role === "municipality_admin");

  const L = (ar, he, en) => language === "ar" ? ar : language === "he" ? he : en;
  const editing = !!id;

  const [form, setForm] = useState({
    title: "",
    description: "",
    goal_amount: 10000,
    status: "planned",
    category: "",
    address: "",
    image_url: null,
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(editing);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${id}`);
        setForm({
          title: data.title || "",
          description: data.description || "",
          goal_amount: data.goal_amount || 0,
          status: data.status || "planned",
          category: data.category || "",
          address: data.address || "",
          image_url: data.image_url || null,
        });
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id, editing]);

  // Block non-municipality users defensively. Wait for AuthContext to hydrate
  // so we don't redirect while user is still loading.
  useEffect(() => {
    if (authLoading) return;
    if (user && !isMunicipality) navigate("/community", { replace: true });
  }, [user, isMunicipality, authLoading, navigate]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB image"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3 || form.description.trim().length < 10) {
      toast.error(L("الرجاء تعبئة العنوان والوصف.", "אנא מלא כותרת ותיאור.", "Please fill title and description.")); return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        goal_amount: parseInt(form.goal_amount, 10) || 0,
        status: form.status,
        category: form.category || null,
        address: form.address || null,
        image_url: form.image_url || null,
      };
      const { data } = editing
        ? await api.patch(`/projects/${id}`, payload)
        : await api.post("/projects", payload);
      toast.success(editing ? L("تم الحفظ", "נשמר", "Saved") : L("تم نشر المشروع", "הפרויקט פורסם", "Project published"));
      if (data && data.id) {
        navigate(`/projects/${data.id}`, { replace: true });
      } else {
        navigate("/community", { replace: true });
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <MobileShell
      topBar={
        <div className="h-14 flex items-center gap-2 px-4">
          <button
            onClick={() => navigate(-1)}
            data-testid="cproj-back-btn"
            className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="font-extrabold text-[20px] text-[var(--te-text)] flex-1">
            {editing ? L("تعديل المشروع", "ערוך פרויקט", "Edit project") : L("مشروع جديد", "פרויקט חדש", "New project")}
          </h1>
        </div>
      }
    >
      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
      ) : (
        <form onSubmit={submit} noValidate className="px-4 pt-4 pb-6 space-y-5">
          <div>
            <label className="te-label">{L("عنوان المشروع", "כותרת הפרויקט", "Project title")}</label>
            <input
              required
              className="te-input"
              value={form.title}
              onChange={onChange("title")}
              maxLength={160}
              data-testid="cproj-title"
              dir="auto"
            />
          </div>
          <div>
            <label className="te-label">{L("الوصف", "תיאור", "Description")}</label>
            <textarea
              required
              className="te-input min-h-[120px] resize-y"
              value={form.description}
              onChange={onChange("description")}
              maxLength={8000}
              data-testid="cproj-description"
              dir="auto"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="te-label">{L("الهدف (₪)", "יעד (₪)", "Goal (₪)")}</label>
              <input
                type="number"
                min={0}
                step={100}
                className="te-input"
                value={form.goal_amount}
                onChange={onChange("goal_amount")}
                data-testid="cproj-goal"
                dir="ltr"
              />
            </div>
            <div>
              <label className="te-label">{L("الحالة", "סטטוס", "Status")}</label>
              <select
                className="te-input"
                value={form.status}
                onChange={onChange("status")}
                data-testid="cproj-status"
              >
                <option value="planned">{L("مخطّط", "מתוכנן", "Planned")}</option>
                <option value="in_progress">{L("قيد التنفيذ", "בביצוע", "In progress")}</option>
                <option value="completed">{L("مكتمل", "הושלם", "Completed")}</option>
              </select>
            </div>
          </div>
          <div>
            <label className="te-label">{L("الفئة (اختياري)", "קטגוריה (אופציונלי)", "Category (optional)")}</label>
            <input
              className="te-input"
              value={form.category}
              onChange={onChange("category")}
              placeholder={L("بنية تحتية، صحة، تعليم…", "תשתיות, בריאות, חינוך…", "infrastructure, health, education…")}
              data-testid="cproj-category"
              dir="auto"
            />
          </div>
          <div>
            <label className="te-label">{L("العنوان (اختياري)", "כתובת (אופציונלי)", "Address (optional)")}</label>
            <input
              className="te-input"
              value={form.address}
              onChange={onChange("address")}
              data-testid="cproj-address"
              dir="auto"
            />
          </div>
          <div>
            <label className="te-label">{L("صورة الغلاف (اختياري)", "תמונת רקע (אופציונלי)", "Cover image (optional)")}</label>
            {form.image_url ? (
              <div className="relative inline-block">
                <img src={form.image_url} alt="" className="rounded-2xl max-h-56 border border-[var(--te-divider)]" />
                <button type="button" onClick={() => setForm((f) => ({ ...f, image_url: null }))} className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow-sm border border-[var(--te-divider)]">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label className="te-btn-secondary inline-flex items-center gap-2 cursor-pointer" data-testid="cproj-upload-image">
                <ImagePlus size={16} /> {L("ارفع صورة", "העלה תמונה", "Upload image")}
                <input type="file" accept="image/*" className="hidden" onChange={onFileChange} />
              </label>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="te-btn-secondary flex-1 justify-center"
              data-testid="cproj-cancel"
            >
              {L("إلغاء", "ביטול", "Cancel")}
            </button>
            <button
              type="submit"
              disabled={busy}
              className="te-btn-primary flex-1 justify-center"
              data-testid="cproj-submit"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              {editing ? L("حفظ", "שמור", "Save") : L("نشر المشروع", "פרסם", "Publish")}
            </button>
          </div>
        </form>
      )}
    </MobileShell>
  );
}
